import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, ArrowRight } from "lucide-react";
import TrustBadges from "./TrustBadges";

/**
 * RecommendedConciergeCard — used in the AI Concierge plan response to surface
 * "Recommended Match" suggestions.
 *
 * Shows ONLY badges the concierge has actually earned (no fake badges). When
 * the concierge isn't approved + profile-complete, this card is intentionally
 * hidden by the parent (we never recommend incomplete profiles).
 *
 * Two CTAs:
 *   • "View Concierge" → /concierge/{slug}  (full public profile)
 *   • "Start Planning"  → /ai?q=...           (re-enter AI with a focused request)
 */
export default function RecommendedConciergeCard({ c, matchReason }) {
  const slug = slugify(c.name);
  const profileHref = `/concierge/${slug}`;
  const firstName = (c.name || "").split(" ")[0] || "this concierge";
  const startHref = `/ai?q=${encodeURIComponent(`I want to plan an experience with ${firstName}.`)}`;
  const specs = (c.specializes || c.categories || []).slice(0, 4);
  const moments = c.moments_created || c.completed_experiences || 0;
  const rating = Number(c.rating || 0).toFixed(1);

  return (
    <div
      data-testid={`recommended-concierge-${slug}`}
      className="rounded-3xl bg-[#FFF8EE] border border-[#00AEEF]/30 shadow-luxury overflow-hidden flex flex-col"
    >
      <div className="bg-[#00AEEF]/8 px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] text-[#00AEEF] font-semibold flex items-center gap-2">
        ✦ Recommended Match
      </div>

      <div className="p-5 flex gap-4">
        {c.photo && (
          <Link to={profileHref} className="shrink-0">
            <img
              src={c.photo}
              alt={c.name}
              className="w-20 h-20 rounded-2xl object-cover hover:opacity-95 transition"
            />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          <Link to={profileHref} className="font-serif text-2xl text-[#071A2D] leading-tight hover:underline">
            {c.name}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#071A2D]/65">
            {(c.city || c.state) && (
              <span className="inline-flex items-center gap-1"><MapPin size={11}/>{c.city}{c.state ? `, ${c.state}` : ""}</span>
            )}
            <span className="inline-flex items-center gap-1">
              <Star size={11} fill="#C9A45C" color="#C9A45C"/> {rating}
            </span>
            {moments > 0 && <span>· {moments} moments created</span>}
          </div>
          <div className="mt-2.5">
            <TrustBadges provider={c} size="sm"/>
          </div>
        </div>
      </div>

      {specs.length > 0 && (
        <div className="px-5 pb-3">
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold mb-1.5">Specializes</div>
          <div className="flex flex-wrap gap-1.5">
            {specs.map((s) => (
              <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-[#064E3B]/8 text-[#064E3B]">{s}</span>
            ))}
          </div>
        </div>
      )}

      {matchReason && (
        <div className="px-5 pb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF7A45] font-semibold mb-1">Why we matched</div>
          <p className="text-sm text-[#071A2D]/80 leading-snug italic">&ldquo;{matchReason}&rdquo;</p>
        </div>
      )}

      <div className="mt-auto px-5 pb-5 flex gap-2 flex-wrap">
        <Link
          to={profileHref}
          data-testid={`rec-view-concierge-${slug}`}
          className="flex-1 min-w-[120px] text-center rounded-full border border-[#064E3B] text-[#064E3B] px-4 py-2 text-sm font-semibold hover:bg-[#064E3B]/5 transition"
        >
          View Concierge
        </Link>
        <Link
          to={startHref}
          data-testid={`rec-start-planning-${slug}`}
          className="flex-1 min-w-[120px] text-center rounded-full bg-[#FF7A45] text-[#FFF8EE] px-4 py-2 text-sm font-semibold hover:brightness-110 transition inline-flex items-center justify-center gap-1.5"
        >
          Start With This Concierge <ArrowRight size={12}/>
        </Link>
      </div>
    </div>
  );
}

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .join("-") || "concierge";
}
